import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
} from '@angular/core';
import { NotificationGroupSync } from '../../helpers/notification-group-sync';
import { WalletManager } from '../../managers/wallet.manager';
import {
  WALLET_LABELS,
  WalletTypeEnum,
} from '../../models/enums/wallet-type.enum';
import { TruncateWalletAddressPipe } from '../../pipes/truncate-wallet-address.pipe';
import {
  KcAlertComponent,
  KcButtonComponent,
  KcIconButtonComponent,
  NotificationService,
} from '@kaspacom/ui-kit';
import {
  StatusTagStatus,
  statusTagClass,
  statusTagSelected,
} from '../../shared/status-tag';
import { KcWalletAvatarComponent } from '../defi-wallet-avatar/defi-wallet-avatar.component';

@Component({
  selector: 'kcc20-wallet-connect',
  standalone: true,
  imports: [
    TruncateWalletAddressPipe,
    KcAlertComponent,
    KcButtonComponent,
    KcIconButtonComponent,
    KcWalletAvatarComponent,
  ],
  templateUrl: './wallet-connect.component.html',
  styleUrl: './wallet-connect.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalletConnectComponent {
  private readonly walletManager = inject(WalletManager);
  private readonly notifications = new NotificationGroupSync(
    inject(NotificationService),
    inject(DestroyRef),
  );

  readonly wallets = computed(() =>
    this.walletManager.getAvailableWalletOptions(),
  );
  readonly session = this.walletManager.signedWalletSignal;
  readonly connecting = this.walletManager.isConnectingToWalletState;
  readonly connectingWalletType = this.walletManager.connectingWalletTypeSignal;
  readonly authError = this.walletManager.authErrorSignal;
  readonly networkPrompt = this.walletManager.walletNetworkPromptSignal;
  readonly walletLabels = WALLET_LABELS;

  readonly TagStatus = StatusTagStatus;
  readonly statusTagClass = statusTagClass;
  readonly statusTagSelected = statusTagSelected;

  constructor() {
    // authErrorSignal is owned by WalletManager (a root singleton with no
    // natural destroy boundary of its own); this component mirrors it into a
    // toast instead, since it's mounted once for the app's lifetime and can
    // own the toast's cleanup via its own DestroyRef.
    effect(() => {
      const text = this.authError();
      this.notifications.sync(
        'auth',
        'Wallet',
        text ? [{ variant: 'error', text }] : [],
      );
    });
  }

  connect(walletType: WalletTypeEnum): void {
    void this.walletManager.connectWallet(walletType);
  }

  disconnect(): void {
    void this.walletManager.disconnect();
  }

  copyWalletAddress(address: string): void {
    void navigator.clipboard.writeText(address);
  }

  logoFor(walletType: WalletTypeEnum): string {
    return (
      this.wallets().find((wallet) => wallet.type === walletType)?.logo ?? ''
    );
  }
}
